import HomeView from "./views/HomeView.vue";
import NewUserDashboardView from "./views/NewUserDashboardView.vue";
import LoginView from "./views/LoginView.vue";
import AdminLayoutView from "./views/admin/AdminLayoutView.vue";
import AdminLoginView from "./views/admin/AdminLoginView.vue";
import TwoFactorView from "./views/TwoFactorView.vue";
import TwoFactorOnboardView from "./views/TwoFactorOnboardView.vue";
import AdminTwoFactorView from "./views/admin/AdminTwoFactorView.vue";
import AdminTwoFactorOnboardView from "./views/admin/AdminTwoFactorOnboardView.vue";
import AdminHomeView from "./views/admin/AdminHomeView.vue";
import AgentsView from "./views/admin/AgentsView.vue";
import KnocsView from "./views/admin/KnocsView.vue";
import CreateKnocView from "./views/admin/CreateKnocView.vue";
import CreateKnocLinuxView from "./views/admin/CreateKnocLinuxView.vue";
import CreateKnocWindowsView from "./views/admin/CreateKnocWindowsView.vue";
import CreateKnocApplicationView from "./views/admin/CreateKnocApplicationView.vue";
import CreateKnocProxyView from "./views/admin/CreateKnocProxyView.vue";
import CreateKnocScriptView from "./views/admin/CreateKnocScriptView.vue";
import GroupsView from "./views/admin/GroupsView.vue";
import UsersView from "./views/admin/UsersView.vue";
import AdminsView from "./views/admin/AdminsView.vue";
import LogsView from "./views/admin/LogsView.vue";
import ApiKeysView from "./views/admin/ApiKeysView.vue";
import SettingsView from "./views/admin/SettingsView.vue";
import UpdateKnocView from "./views/admin/UpdateKnocView.vue";

import globals from "./globals";
import api from "./services/api";
import { createRouter, createWebHistory, RouteLocationNormalizedGeneric, RouteRecordRaw } from "vue-router";
import { useAuthStore } from "./pinia/auth.ts";
import { useAdminStore } from "./pinia/admin.ts";

declare module "vue-router" {
  interface RouteMeta {
    title?: string;
    favicon?: string;
    requiresUserLogin?: boolean;
    requiresAdminLogin?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/home",
    name: "Home",
    component: HomeView,
    meta: { title: "Dashboard - Knocknoc", requiresUserLogin: true },
  },
  {
    path: "/home/new",
    name: "NewUserDashboard",
    component: NewUserDashboardView,
    meta: { title: "Dashboard - Knocknoc", requiresUserLogin: true },
  },
  { path: "/otp/verify", name: "TwoFactor", component: TwoFactorView, meta: { title: "Authenticate - Knocknoc" } },
  {
    path: "/otp/onboard",
    name: "TwoFactorOnboard",
    component: TwoFactorOnboardView,
    meta: { title: "Authenticate - Knocknoc" },
  },
  {
    path: "/otp/setup",
    name: "TwoFactorSetup",
    component: TwoFactorOnboardView,
    props: { fromKnoc: true },
    meta: { title: "Set up 2FA - Knocknoc", requiresUserLogin: true },
  },
  { path: "/", name: "Login", component: LoginView, meta: { title: "Login - Knocknoc" } },
  {
    path: "/admin",
    component: AdminLayoutView,
    redirect: { name: "AdminLogin" },
    meta: { title: "Knocknoc", favicon: "/assets/images/Knocknoc_Symbol_Large_redish.png" },
    children: [
      { path: "login", name: "AdminLogin", component: AdminLoginView, meta: { title: "Login - Knocknoc" } },
      {
        path: "otp/verify",
        name: "AdminTwoFactor",
        component: AdminTwoFactorView,
        meta: { title: "Authenticate - Knocknoc" },
      },
      {
        path: "otp/onboard",
        name: "AdminTwoFactorOnboard",
        component: AdminTwoFactorOnboardView,
        meta: { title: "Authenticate - Knocknoc" },
      },

      {
        path: "home",
        name: "AdminHome",
        component: AdminHomeView,
        meta: { title: "Home - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "agents",
        name: "AdminAgents",
        component: AgentsView,
        meta: { title: "Agents - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "knocs",
        name: "AdminKnocs",
        component: KnocsView,
        meta: { title: "Knocs - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "knocs/new",
        name: "AdminKnocsNew",
        component: CreateKnocView,
        meta: { title: "Create knoc - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "knocs/new/linux",
        name: "AdminKnocsNewLinux",
        component: CreateKnocLinuxView,
        meta: { title: "Create linux knoc - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "knocs/new/windows",
        name: "AdminKnocsNewWindows",
        component: CreateKnocWindowsView,
        meta: { title: "Create windows knoc - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "knocs/new/application",
        name: "AdminKnocsNewApplication",
        component: CreateKnocApplicationView,
        meta: { title: "Create application knoc - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "knocs/new/proxy",
        name: "AdminKnocsNewProxy",
        component: CreateKnocProxyView,
        meta: { title: "Create proxy knoc - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "knocs/new/script",
        name: "AdminKnocsNewScripts",
        component: CreateKnocScriptView,
        meta: { title: "Create script knoc - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "knocs/:knocId/update",
        name: "AdminKnocsUpdate",
        component: UpdateKnocView,
        meta: { title: "Update knoc - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "groups",
        name: "AdminGroups",
        component: GroupsView,
        meta: { title: "Groups - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "users",
        name: "AdminUsers",
        component: UsersView,
        meta: { title: "Users - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "admins",
        name: "AdminAdmins",
        component: AdminsView,
        meta: { title: "Admins - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "logs",
        name: "AdminLogs",
        component: LogsView,
        meta: { title: "Logs - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "api-keys",
        name: "AdminApiKeys",
        component: ApiKeysView,
        meta: { title: "Api keys - Knocknoc", requiresAdminLogin: true },
      },
      {
        path: "settings",
        name: "AdminSettings",
        component: SettingsView,
        meta: { title: "Settings - Knocknoc", requiresAdminLogin: true },
      },
      { path: "/admin/:pathMatch(.*)*", redirect: "/admin/home" },
    ],
  },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

function handleRouteMetadata(to: RouteLocationNormalizedGeneric) {
  const toRouteChain = to.matched;
  let title = "Knocknoc";

  // If the route doesn't have a title, get it from the closest parent
  for (let i = toRouteChain.length - 1; i >= 0; i--) {
    const route = toRouteChain[i];
    if (route.meta?.title) {
      title = route.meta.title;
      break;
    }
  }

  document.title = title;

  let favicon = "/assets/images/Knocknoc_Symbol_Large_purple.png";

  // If the route doesn't have a favicon, get it from the closest parent
  for (let i = toRouteChain.length - 1; i >= 0; i--) {
    const route = toRouteChain[i];
    if (route.meta?.favicon) {
      favicon = route.meta.favicon;
      break;
    }
  }

  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = favicon;
}

router.beforeEach(async (to, _, next) => {
  handleRouteMetadata(to);

  const authStore = useAuthStore();
  const adminStore = useAdminStore();
  const user = (authStore.user != null && (await authStore.fetchStatus())) || null;
  const adminUser = (adminStore.user != null && (await adminStore.fetchStatus())) || null;

  if (adminUser != null) {
    // authenticated user at this point
    if (to.name === "AdminLogin") {
      // If a user is already logged in and tries to navigate to the login page,
      // redirect them to the appropriate Home page to prevent them from seeing the login screen unnecessarily.
      next({ name: "AdminHome" });
      return;
    }
    if (adminUser.TotpEnabled && !adminUser.TotpEnrolmentDeferable && !adminUser.AdminTotpVerified) {
      if (to.name !== "AdminTwoFactorOnboard") {
        next({ name: "AdminTwoFactorOnboard" });
        return;
      }
    }
  }

  if (user != null) {
    if (to.name === "Login") {
      // If a user is already logged in and tries to navigate to the login page,
      // redirect them to the appropriate Home page to prevent them from seeing the login screen unnecessarily.
      const routeName = GetHomeRouteName(user);
      next({ name: routeName, params: to.params, query: to.query });
      return;
    }
  }

  // At this point the user is not authenticated. Redirect them to the appropriate login page if the page is protected
  if (to.meta) {
    if (!adminUser && to.meta.requiresAdminLogin) {
      next({ name: "AdminLogin" });
      return;
    }
    if (!user && to.meta.requiresUserLogin) {
      next({ name: "Login", params: to.params, query: to.query });
      return;
    }
  }

  // For all other cases, proceed with navigation
  next();
});

router.afterEach(() => {
  api.get("/system_status").then(
    (response) => {
      globals.checkJsBundleHash(response.data.JsBundleHash);
    },
    () => {
      return null;
    },
  );
});

function GetHomeRouteName(user) {
  if (user) {
    if (user.NewUserDashboard) {
      return "NewUserDashboard";
    }
  }

  return "Home";
}
